import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { FormikProvider, useFormik, useFormikContext } from 'formik';
import { TSourceType } from 'src/features/customReport';
import { IClipping } from 'src/models/onlineMedia';
import {
  IGroupObject,
  IObject,
  TObjectFilter
} from 'src/models/objectManagement';
import * as Yup from 'yup';
import { useAlert } from 'src/hooks/useAlert';
import SelectClippingSection from './SelectClippingSection';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SelectGroupsSection from './SelectGroupsSection';
import CloseIcon from '@mui/icons-material/Close';
import SelectObjectOrGroupSection from './SelectObjectOrGroupSection';
import {
  reducerUpdateCurrentScreen,
  reducerUpdateDashboardList,
  reducerUpdateObjectManagementDashboardSelectedById,
  ScreenKey
} from 'src/features/customDashboard';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import SelectObjectSection from './SelectObjectSection';
import { objectTypeOptions } from 'src/content/settings/MonitoredAssets/components/HeaderObjects';
import { reducerUpdateSelectedObjectType } from 'src/features/objectManagement';
import { TCustomDashboardSourceType } from 'src/models/customDashboard';
import { IAddCustomDashboardPayload } from 'src/services/useCustomDashboard';
import ProductTourTooltip from 'src/components/ProductTourTooltip';
import { CUSTOM_DASHBOARD } from 'src/route';
import { customDashboardDummy } from 'src/constants/dummies';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

interface ModalCreateCustomDashboardProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: IAddCustomDashboardPayload) => void;
}

type TDashboardSourceType = TSourceType | 'offline_media';

const SourceTypeScreen = () => {
  const { t } = useTranslation();
  const { touched, errors, handleChange, values } =
    useFormikContext<IFormValues>();
  const { isOnTour } = useAppSelector((state) => state.storeProductTour);
  return (
    <Stack gap={2}>
      <FormControl fullWidth size="medium">
        <FormLabel>{t('dashboard name')}</FormLabel>
        <OutlinedInput
          placeholder="Type dashboard name"
          name="dashboardName"
          value={values.dashboardName}
          onChange={!isOnTour ? handleChange : undefined}
          readOnly={isOnTour}
        />
      </FormControl>
      <FormControl fullWidth size="medium">
        <FormLabel>{t('source type')}</FormLabel>
        <Select
          name="sourceType"
          value={values.sourceType}
          onChange={!isOnTour ? handleChange : undefined}
          readOnly={isOnTour}
          displayEmpty
          error={touched.sourceType && Boolean(errors.sourceType)}
        >
          <MenuItem value={'social_media'}>{t('social media')}</MenuItem>
          <MenuItem value={'online_media'}>{t('online media')}</MenuItem>
          <MenuItem value={'offline_media'}>{t('offline media')}</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );
};

interface ScreenConfig<TProps = {}> {
  title: string;
  Component: React.FC<TProps>;
  next?: (values: IFormValues) => ScreenKey | undefined;
  getProps?: (formik: ReturnType<typeof useFormik>) => TProps;
}

const screenConfig: Record<ScreenKey, ScreenConfig> = {
  sourceType: {
    title: 'create dashboard',
    Component: SourceTypeScreen,
    next: (values) => {
      if (values.sourceType === 'social_media') return 'socialMedia';
      if (values.sourceType === 'online_media') return 'onmClipping';
      if (values.sourceType === 'offline_media') return 'ofmClipping';
    }
  },
  socialMedia: {
    title: 'Select Object or Group',
    Component: SelectObjectOrGroupSection
  },
  group: { title: 'Select Groups', Component: SelectGroupsSection },
  object: { title: 'Select Objects', Component: SelectObjectSection },
  onmClipping: {
    title: 'Select Online Media Topic (Clipping)',
    Component: () => <SelectClippingSection mediaType="onlineMedia" />
  },
  ofmClipping: {
    title: 'Select Offline Media Topic (Clipping)',
    Component: () => <SelectClippingSection mediaType="offlineMedia" />
  }
};

export interface IFormValues {
  dashboardName: string;
  sourceType: TDashboardSourceType;
  objects: IObject[];
  objectGroups: IGroupObject[];
  onmClipping: IClipping | null;
  ofmClipping: IClipping | null;
}

const validationSchema = Yup.object().shape({
  dashboardName: Yup.string().required('Dashboard name is required'),
  sourceType: Yup.string().required('Source type is required'),
  objects: Yup.array().when('sourceType', {
    is: 'object',
    then: Yup.array().min(1, 'At least one object is required')
  }),
  objectGroups: Yup.array().when('sourceType', {
    is: 'group',
    then: Yup.array().min(1, 'At least one group is required')
  }),
  onmClippings: Yup.array().when('sourceType', {
    is: 'online_media',
    then: Yup.array().min(1, 'At least one online media is required')
  }),
  ofmClippings: Yup.array().when('sourceType', {
    is: 'offline_media',
    then: Yup.array().min(1, 'At least one offline media is required')
  })
});

const convertSourceTypeToDashboardSourceType = (
  sourceType: TDashboardSourceType
): TCustomDashboardSourceType => {
  switch (sourceType) {
    case 'social_media':
      return 'socmed';
    case 'online_media':
      return 'onm';
    case 'offline_media':
      return 'ofm';
    default:
      return 'socmed';
  }
};

const ModalCreateCustomDashboard = ({
  open,
  onClose,
  onSubmit
}: ModalCreateCustomDashboardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { handleClickAlert } = useAlert();
  const navigate = useNavigate();
  const projectId = useAppSelector((state) => state.users.project.id);
  const timezone = useAppSelector((state) => state.users.project.timezone);
  const { currentScreen, tempSelectedSocialProfiles, tempSelectedKeywords } =
    useAppSelector((state) => state.storeCustomDashboard);
  const { selectedObjectType, objectList } = useAppSelector(
    (state) => state.objectManagement
  );
  const dashboardListResponse = useAppSelector(
    (state) => state.storeCustomDashboard.dashboardList
  );
  const dispatch = useAppDispatch();
  const formik = useFormik<IFormValues>({
    initialValues: {
      dashboardName: '',
      sourceType: 'social_media',
      onmClipping: null,
      ofmClipping: null,
      objects: [],
      objectGroups: []
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const clipping =
        values.sourceType === 'online_media'
          ? values.onmClipping
          : values.ofmClipping;

      const isObject = values.objects.length > 0;
      const isGroup = values.objectGroups.length > 0;

      const finalValues: IAddCustomDashboardPayload = {
        projectId,
        displayName: values.dashboardName,
        dashboardSourceType: convertSourceTypeToDashboardSourceType(
          values.sourceType
        ),
        objectIds: isObject ? values.objects.map((object) => object.id) : null,
        objectGroupIdsOption: isGroup
          ? values.objectGroups.map((group) => group.id)
          : null,
        clippingId: clipping?.id ?? null
      };

      onSubmit(finalValues);
      handleClose();
    }

  });

  const { handleSubmit, values, resetForm, errors } = formik;
  const { title, Component, next } = screenConfig[currentScreen];

  const getParentScreen = (currentScreen: ScreenKey): ScreenKey | null => {
    switch (currentScreen) {
      case 'socialMedia':
      case 'onmClipping':
      case 'ofmClipping':
        return 'sourceType';
      case 'group':
      case 'object':
        return 'socialMedia';
      default:
        return null;
    }
  };

  const handleBack = () => {
    const parentScreen = getParentScreen(currentScreen);
    if (parentScreen) {
      dispatch(reducerUpdateCurrentScreen(parentScreen));
    }
  };

  const handleClose = () => {
    onClose();
    dispatch(reducerUpdateCurrentScreen('sourceType'));
    resetForm();
  };

  const handleNext = () => {
    if (currentScreen === 'sourceType' && !values.dashboardName.trim()) {
      handleClickAlert({
        message: 'Dashboard name is required',
        vertical: 'top',
        horizontal: 'center',
        severity: 'error'
      });
      return;
    }

    if (currentScreen === 'object') {
      const selectedObjects = objectList.data.filter(
        (obj) =>
          tempSelectedSocialProfiles.includes(obj.id) ||
          tempSelectedKeywords.includes(obj.id)
      );
      formik.setFieldValue('objects', selectedObjects);
      handleBack();
      return;
    }

    if (currentScreen === 'group') {
      handleBack();
      return;
    }

    if (next) {
      const nextScreen = next(values);
      if (nextScreen) {
        dispatch(reducerUpdateCurrentScreen(nextScreen));
      } else {
        handleSubmit(); // final submit if no next
      }
    } else {
      handleSubmit(); // if screen has no next, submit
    }
  };

  const handleChangeObjectType = (
    event: SelectChangeEvent<string>,
    child: React.ReactNode
  ) => {
    const selectedValue = event.target.value;
    dispatch(reducerUpdateSelectedObjectType(selectedValue as TObjectFilter));
  };

  const hideCloseButton =
    currentScreen === 'object' ||
    currentScreen === 'group' ||
    currentScreen === 'onmClipping' ||
    currentScreen === 'ofmClipping';

  const {
    isOnTour,
    currentTour: { stepNumber, menu }
  } = useAppSelector((state) => state.storeProductTour);
  const isCurrentlyOnTourStep4 =
    stepNumber === 4 && isOnTour && menu === 'customization';
  const isCurrentlyOnTourStep5 =
    stepNumber === 5 && isOnTour && menu === 'customization';
  const isCurrentlyOnTourStep6 =
    stepNumber === 6 && isOnTour && menu === 'customization';
  const isCurrentlyOnTourStep7 =
    stepNumber === 7 && isOnTour && menu === 'customization';

  const onClickStep4 = () => {
    const nextScreen = next(values);
    if (nextScreen) {
      dispatch(reducerUpdateCurrentScreen(nextScreen));
    }
  };

  const onClickStep5 = () => {
    dispatch(reducerUpdateCurrentScreen('object'));
  };

  const onClickStep6 = () => {
    formik.setFieldValue('objects', objectList.data.slice(0, 2));
    handleBack();
  };

  const menuName = timezone === 7 ? 'Custom Dashboard' : 'Instant Insight';

  const onClickStep7 = () => {
    dispatch(
      reducerUpdateDashboardList({
        ...dashboardListResponse,
        data: [
          ...dashboardListResponse.data,
          { ...customDashboardDummy, customDashboardWidgetMetadataList: [] }
        ]
      })
    );
    onClose();
    handleClickAlert({
      horizontal: 'center',
      vertical: 'top',
      message: `${menuName} created successfully`,
      severity: 'success'
    });
    dispatch(
      reducerUpdateObjectManagementDashboardSelectedById(customDashboardDummy)
    );
    navigate(`${CUSTOM_DASHBOARD}/${customDashboardDummy.id}`);
  };

  const handleClickNext = () => {
    if (isCurrentlyOnTourStep4) return onClickStep4();
    if (isCurrentlyOnTourStep5) return onClickStep5();
    if (isCurrentlyOnTourStep6) return onClickStep6();
    if (isCurrentlyOnTourStep7) return onClickStep7();
  };

  return (
    <Dialog
      onClose={!isOnTour ? handleClose : undefined}
      open={open}
      fullWidth
      maxWidth={'md'}
    >
      {!hideCloseButton && (
        <IconButton
          aria-label="close"
          onClick={!isOnTour ? handleClose : undefined}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
      <form onSubmit={!isOnTour ? handleSubmit : undefined}>
        <ProductTourTooltip
          maxWidth={
            isCurrentlyOnTourStep5 ||
              isCurrentlyOnTourStep6 ||
              isCurrentlyOnTourStep7
              ? 150
              : 600
          }
          placement={isCurrentlyOnTourStep4 ? 'top' : 'right-start'}
          open={
            isCurrentlyOnTourStep4 ||
            isCurrentlyOnTourStep5 ||
            isCurrentlyOnTourStep6 ||
            isCurrentlyOnTourStep7
          }
          onClick={handleClickNext}
          onClose={handleClose}
        >
          <DialogTitle
            sx={{
              m: !isOnTour ? theme.spacing(3, 3, 0) : undefined,
              p: isOnTour ? theme.spacing(3, 3, 1) : 0
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <Stack direction="row" alignItems="center" gap={2}>
                {currentScreen !== 'sourceType' && (
                  <IconButton
                    onClick={!isOnTour ? handleBack : undefined}
                    sx={{
                      color: theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover
                      }
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                )}
              </Stack>

              {currentScreen === 'object' ? (
                <Select
                  id="objectType"
                  name="objectType"
                  value={selectedObjectType}
                  onChange={handleChangeObjectType}
                  sx={{ minWidth: '140px', borderRadius: theme.spacing(2) }}
                >
                  <MenuItem
                    key="placeholder"
                    id="placeholder"
                    value=""
                    disabled
                  >
                    {t('object type')}
                  </MenuItem>
                  {objectTypeOptions.map(({ id, value, label }) => (
                    <MenuItem key={id} id={id} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              ) : null}
            </Stack>
          </DialogTitle>
        </ProductTourTooltip>
        <DialogContent>
          <Stack spacing={1}>
            <Typography variant="h3" textAlign="center">
              {t(title)}
            </Typography>
            <FormikProvider value={formik}>
              <Component />
            </FormikProvider>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            size="large"
            sx={{
              background: theme.palette.primary.main,
              px: theme.spacing(4)
            }}
            onClick={!isOnTour ? handleNext : undefined}
          >
            {next ? t('next') : t('submit')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ModalCreateCustomDashboard;
